import { useState, useCallback, useEffect } from "react";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import {
  RefreshCw, AlertTriangle, Cpu, Globe,
  ChevronRight, X, Zap, Clock, Search
} from "lucide-react";
import { toast } from "sonner";
import { API } from "@/App";
import { Link } from "react-router-dom";
import { SeverityBadge, StatusBadge } from "./Dashboard";

export function ClusterDashboard() {
  const [clusters, setClusters] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selectedCluster, setSelectedCluster] = useState(null);
  const [clusterDetail, setClusterDetail] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [analyzing, setAnalyzing] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [gameFilter, setGameFilter] = useState("STAR_ATLAS_HOLOSIM");

  const fetchClusters = useCallback(async () => {
    try {
      setLoading(true);
      const res = await axios.get(`${API}/admin/clusters`, { withCredentials: true });
      setClusters(res.data);
    } catch (error) {
      console.error("Error fetching clusters:", error);
      toast.error(error.response?.data?.detail || "Failed to load clusters");
    } finally {
      setLoading(false);
    }
  }, []);

  // Auto-load on mount
  useEffect(() => {
    fetchClusters();
  }, [fetchClusters]);

  const openClusterDetail = async (clusterId) => {
    try {
      setDetailLoading(true);
      setSelectedCluster(clusterId);
      const res = await axios.get(`${API}/admin/clusters/${clusterId}`, { withCredentials: true });
      setClusterDetail(res.data);
    } catch (error) {
      toast.error("Failed to load cluster detail");
      setSelectedCluster(null);
    } finally {
      setDetailLoading(false);
    }
  };

  const handleAnalyze = async () => {
    if (!selectedCluster) return;
    try {
      setAnalyzing(true);
      const res = await axios.post(`${API}/admin/clusters/${selectedCluster}/analyze`, {}, { withCredentials: true });
      toast.success("Cluster analysis complete");
      setClusterDetail(prev => prev ? { ...prev, analysis: res.data.analysis } : prev);
    } catch (error) {
      toast.error(error.response?.data?.detail || "AI analysis failed");
    } finally {
      setAnalyzing(false);
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case "ACTIVE": return "text-orange-400 bg-orange-500/10 border-orange-500/30";
      case "FIXED": return "text-green-400 bg-green-500/10 border-green-500/30";
      default: return "text-slate-400 bg-slate-500/10 border-slate-500/30";
    }
  };

  const getSeverityColor = (severity) => {
    switch (severity) {
      case "Critical": return "text-red-400";
      case "High": return "text-orange-400";
      case "Medium": return "text-yellow-400";
      default: return "text-blue-400";
    }
  };

  // Group clusters by game with search filter
  const filteredClusters = clusters.filter(c => {
    if (gameFilter !== "all" && c.game !== gameFilter) return false;
    if (searchQuery) {
      const q = searchQuery.toLowerCase();
      return (c.cluster_title?.toLowerCase().includes(q) || c.display_code?.toLowerCase().includes(q) || c.classification?.toLowerCase().includes(q));
    }
    return true;
  });
  const ue5Clusters = filteredClusters.filter(c => c.game === "STAR_ATLAS_UE5");
  const holosimClusters = filteredClusters.filter(c => c.game === "STAR_ATLAS_HOLOSIM");

  const renderClusterRow = (cluster) => (
    <button
      key={cluster.cluster_id}
      onClick={() => openClusterDetail(cluster.cluster_id)}
      className="w-full text-left p-3 rounded-sm border border-border/40 bg-card hover:border-primary/30 hover:bg-muted/30 transition-colors"
      data-testid={`cluster-row-${cluster.display_code}`}
    >
      <div className="flex items-center gap-3">
        <span className="text-xs font-mono text-primary bg-primary/10 px-2 py-0.5 rounded-sm border border-primary/20 flex-shrink-0">
          {cluster.display_code}
        </span>
        <span className={`text-[10px] font-mono uppercase px-1.5 py-0.5 rounded-sm border ${getStatusColor(cluster.status)}`}>
          {cluster.status}
        </span>
        <span className={`text-[10px] font-mono font-bold ${getSeverityColor(cluster.severity)}`}>
          {cluster.severity}
        </span>
        <span className="text-xs text-foreground font-body truncate flex-1 min-w-0">
          {cluster.cluster_title}
        </span>
        <div className="flex items-center gap-3 flex-shrink-0">
          <span className="text-[10px] font-mono text-muted-foreground">
            {cluster.active_bugs}/{cluster.total_bugs} active
          </span>
          <span className="text-[10px] font-mono text-muted-foreground">
            {cluster.last_seen_version}
          </span>
          <ChevronRight className="w-3 h-3 text-muted-foreground" />
        </div>
      </div>
    </button>
  );

  return (
    <div className="space-y-6">
      {/* Header with Search and Filters */}
      <div className="flex flex-col md:flex-row items-start md:items-center gap-3">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-muted-foreground" />
          <Input
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            placeholder="Search clusters by title, code, or classification..."
            className="pl-9 bg-black/20 border-border/50 font-mono text-xs"
            data-testid="cluster-search-input"
          />
        </div>
        <Select value={gameFilter} onValueChange={setGameFilter}>
          <SelectTrigger className="w-44 bg-black/20 border-border/50 font-mono text-xs" data-testid="cluster-game-filter">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Games</SelectItem>
            <SelectItem value="STAR_ATLAS_HOLOSIM">Holosim</SelectItem>
            <SelectItem value="STAR_ATLAS_UE5">UE5</SelectItem>
          </SelectContent>
        </Select>
        <Button variant="outline" onClick={fetchClusters} disabled={loading} className="font-mono text-xs" data-testid="refresh-clusters-btn">
          <RefreshCw className={`w-3 h-3 mr-1 ${loading ? 'animate-spin' : ''}`} />
          Refresh
        </Button>
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-12">
          <RefreshCw className="w-6 h-6 animate-spin text-primary" />
        </div>
      ) : clusters.length === 0 ? (
        <div className="text-center py-12 text-muted-foreground font-mono text-sm">
          No clusters found
        </div>
      ) : (
        <>
          {/* UE5 Clusters */}
          {ue5Clusters.length > 0 && (
            <div>
              <div className="flex items-center gap-2 mb-3">
                <Cpu className="w-4 h-4 text-primary" />
                <h3 className="text-xs font-heading font-bold uppercase tracking-wider text-foreground">
                  Star Atlas — UE5
                </h3>
                <span className="text-[10px] font-mono text-muted-foreground">({ue5Clusters.length})</span>
              </div>
              <div className="space-y-1">
                {ue5Clusters.map(renderClusterRow)}
              </div>
            </div>
          )}

          {/* Holosim Clusters */}
          {holosimClusters.length > 0 && (
            <div>
              <div className="flex items-center gap-2 mb-3">
                <Globe className="w-4 h-4 text-cyan-400" />
                <h3 className="text-xs font-heading font-bold uppercase tracking-wider text-foreground">
                  Star Atlas — Holosim
                </h3>
                <span className="text-[10px] font-mono text-muted-foreground">({holosimClusters.length})</span>
              </div>
              <div className="space-y-1">
                {holosimClusters.map(renderClusterRow)}
              </div>
            </div>
          )}
        </>
      )}

      {/* Cluster Detail Modal */}
      {selectedCluster && (
        <div
          className="fixed inset-0 bg-black/85 z-50 flex items-center justify-center p-4"
          onClick={() => { setSelectedCluster(null); setClusterDetail(null); }}
        >
          <div
            className="bg-card border-2 border-purple-500/40 rounded-sm w-full max-w-5xl max-h-[90vh] overflow-hidden flex flex-col shadow-2xl shadow-purple-500/10"
            onClick={(e) => e.stopPropagation()}
            data-testid="cluster-detail-modal"
          >
            {detailLoading ? (
              <div className="flex items-center justify-center py-20">
                <RefreshCw className="w-6 h-6 animate-spin text-primary" />
              </div>
            ) : clusterDetail ? (
              <>
                {/* Modal Header */}
                <div className="p-5 border-b border-purple-500/20 bg-purple-500/5 flex-shrink-0">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <span className="text-sm font-mono text-primary bg-primary/10 px-2 py-1 rounded-sm border border-primary/20">
                        {clusterDetail.display_code}
                      </span>
                      <div>
                        <h3 className="font-heading text-base font-bold uppercase tracking-wider text-foreground">
                          {clusterDetail.primary_bug?.title || "Cluster Detail"}
                        </h3>
                        <p className="text-xs text-muted-foreground font-mono mt-1">
                          {clusterDetail.total_bugs} bugs | {clusterDetail.active_bugs} active | {clusterDetail.classification} | {clusterDetail.first_seen_version} → {clusterDetail.last_seen_version}
                        </p>
                      </div>
                    </div>
                    <button onClick={() => { setSelectedCluster(null); setClusterDetail(null); }} className="p-2 text-muted-foreground hover:text-foreground">
                      <X className="w-5 h-5" />
                    </button>
                  </div>

                  {/* Quick Insights */}
                  {clusterDetail.insights?.length > 0 && (
                    <div className="mt-3 flex flex-wrap gap-2">
                      {clusterDetail.insights.map((insight, i) => (
                        <span key={i} className="text-[10px] font-mono px-2 py-0.5 bg-amber-500/10 text-amber-400 border border-amber-500/20 rounded-sm flex items-center gap-1">
                          <AlertTriangle className="w-3 h-3" />
                          {insight}
                        </span>
                      ))}
                    </div>
                  )}
                </div>

                {/* Modal Body - Scrollable */}
                <div className="flex-1 overflow-y-auto p-5 space-y-5">
                  {/* AI Analysis Section */}
                  <div className="border border-purple-500/20 rounded-sm">
                    <div className="p-3 border-b border-purple-500/20 bg-purple-500/5 flex items-center justify-between">
                      <h4 className="text-xs font-mono font-bold uppercase tracking-wider text-purple-400 flex items-center gap-2">
                        <Zap className="w-3 h-3" />
                        AI Root Cause Analysis
                      </h4>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={handleAnalyze}
                        disabled={analyzing}
                        className="text-[10px] font-mono text-purple-400 border-purple-500/30 hover:bg-purple-500/10 h-7"
                        data-testid="analyze-cluster-btn"
                      >
                        {analyzing ? <RefreshCw className="w-3 h-3 mr-1 animate-spin" /> : <Zap className="w-3 h-3 mr-1" />}
                        {clusterDetail.analysis ? "Re-analyze" : "Analyze Cluster"}
                      </Button>
                    </div>
                    <div className="p-4">
                      {clusterDetail.analysis ? (
                        <div className="space-y-3">
                          <div>
                            <p className="text-[10px] text-purple-400 font-mono uppercase mb-1 font-bold">Root Cause</p>
                            <p className="text-sm text-foreground font-body bg-purple-500/5 p-3 rounded-sm border border-purple-500/10">{clusterDetail.analysis.root_cause}</p>
                          </div>
                          <div>
                            <p className="text-[10px] text-emerald-400 font-mono uppercase mb-1 font-bold">Suggested Fix</p>
                            <p className="text-sm text-foreground font-body bg-emerald-500/5 p-3 rounded-sm border border-emerald-500/10">{clusterDetail.analysis.suggested_fix}</p>
                          </div>
                          <div className="flex items-center gap-4">
                            {clusterDetail.analysis.confidence && (
                              <span className={`text-[10px] font-mono px-2 py-0.5 rounded-sm border ${
                                clusterDetail.analysis.confidence === 'high' ? 'text-green-400 bg-green-500/10 border-green-500/20' :
                                clusterDetail.analysis.confidence === 'medium' ? 'text-yellow-400 bg-yellow-500/10 border-yellow-500/20' :
                                'text-red-400 bg-red-500/10 border-red-500/20'
                              }`}>
                                Confidence: {clusterDetail.analysis.confidence}
                              </span>
                            )}
                            {clusterDetail.analysis.pattern && (
                              <span className="text-[10px] text-muted-foreground font-mono">{clusterDetail.analysis.pattern}</span>
                            )}
                          </div>
                          <p className="text-[10px] text-muted-foreground font-mono">
                            Analyzed: {clusterDetail.analysis.analyzed_at?.slice(0, 16)} by {clusterDetail.analysis.analyzed_by}
                          </p>
                        </div>
                      ) : (
                        <p className="text-xs text-muted-foreground font-mono text-center py-4">
                          Click "Analyze Cluster" to run AI root cause analysis
                        </p>
                      )}
                    </div>
                  </div>

                  {/* Bug Timeline List */}
                  <div className="border border-border/40 rounded-sm">
                    <div className="p-3 border-b border-border/40 bg-muted/20">
                      <h4 className="text-xs font-mono font-bold uppercase tracking-wider text-muted-foreground flex items-center gap-2">
                        <Clock className="w-3 h-3" />
                        Bug Timeline — {clusterDetail.bugs?.length || 0} bugs (newest first)
                      </h4>
                    </div>
                    <div className="divide-y divide-border/20 max-h-[45vh] overflow-y-auto">
                      {clusterDetail.bugs?.map((bug) => (
                        <div
                          key={bug.id}
                          className="p-3 flex items-center gap-3 hover:bg-muted/10"
                        >
                          {/* Version */}
                          <span className="text-[10px] font-mono text-muted-foreground w-28 flex-shrink-0">
                            {bug.version || "?"}
                          </span>

                          {/* Status */}
                          <StatusBadge status={bug.status} />

                          {/* Title + link */}
                          <Link
                            to={`/bugs/${bug.id}`}
                            onClick={() => { setSelectedCluster(null); setClusterDetail(null); }}
                            className="text-xs text-foreground font-body hover:text-primary truncate flex-1 min-w-0"
                          >
                            {bug.title}
                          </Link>

                          {/* Display code */}
                          <span className="text-[9px] font-mono text-muted-foreground flex-shrink-0 hidden lg:block">
                            {bug.display_code?.slice(0, 35)}
                          </span>

                          {/* Severity */}
                          <SeverityBadge severity={bug.severity} />
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              </>
            ) : null}
          </div>
        </div>
      )}
    </div>
  );
}
